import { useEffect, useRef, useState } from "react";
import "react-toastify/dist/ReactToastify.css";
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Chip,
  ClickAwayListener,
  Divider,
  Grid,
  Paper,
  Popper,
  useMediaQuery,
} from "@mui/material";
import PerfectScrollbar from "react-perfect-scrollbar";
import MainCard from "ui-component/cards/MainCard";
import Transitions from "ui-component/extended/Transitions";
import NotificationList from "./NotificationList";
import { IconBell } from "@tabler/icons";
import { dispatch } from "store";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { openSnackbar } from "store/slices/snackbar";
import useRequestIdGenerater from "customhooks";
import useTimeStamp from "reuseableTable/uniquetimestamp/GenerateUniqueTimeStamp";
import axios from "axios";

// ==============================|| NOTIFICATION ||============================== //

const NotificationSection = () => {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [count, setCount] = useState(0);
  /**
   * anchorRef is used on different componets and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef(null);
  const RequestId = useRequestIdGenerater();
  const { rendomRequestId, generateTimeStamp } = useTimeStamp();

  const handleToggle = () => {
    clearNotification();
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  useEffect(() => {
    // ws://https://dpsapi.entraralumni.com/ws
    const client = new Client({
      brokerURL: "ws://https://dpsapi.alumni.live/ws",
      webSocketFactory: () => new SockJS("https://dpsapi.alumni.live/ws"),
      debug: function (str) {
        console.log(str);
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });
    const messageSound = new Audio(
      "/assets/images/icons/mixkit-bell-notification-933.wav"
    );

    client.onConnect = () => {
      client.subscribe(
        `/topic/notification/${localStorage.getItem("userId")}`,
        (message) => {
          try {
            const updatedPost = JSON.parse(message.body);
            setCount(updatedPost?.count);
            messageSound.play();
            dispatch(
              openSnackbar({
                open: true,
                message: `${updatedPost?.fromUserName} sent you a message`,
                variant: "alert",
                alert: {
                  color: "success",
                },
                close: false,
              })
            );

            // toast.success(`${updatedPost?.fromUserName} sent you a message`, {
            //   position: "top-right",
            //   autoClose: 3000,
            //   hideProgressBar: false,
            //   closeOnClick: true,
            //   pauseOnHover: true,
            //   draggable: true,
            //   type: "success",
            //   theme: "dark",
            //   icon: (
            //     <img
            //       src="https://github.com/chhavientrar/imageslink/assets/124863903/9533d6cb-d463-4b74-a4fe-97d3743909f7"
            //       alt="Custom Icon"
            //       height="25px"
            //       width="25px"
            //     />
            //   ),
            // });
          } catch (error) {
            console.error("Failed to process message:", error);
          }
        }
      );
    };

    client.activate();

    return () => {
      client.deactivate();
    };
  }, []);

  const clearNotification = async () => {
    try {
      const response = await axios.get(
        `/v1/notification/read`,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem(
              "serviceToken"
            )}`,
            "Content-Type": "application/json",
            requestId: RequestId(
              Math.floor(1000 + Math.random() * 9999),
              Math.floor(1000 + Math.random() * 9999 + rendomRequestId)
            ),
          },
        }
      );
      generateTimeStamp();
    } catch (error) {
      generateTimeStamp();
      console.error("Failed to fetch data:", error);
    }
  }
  const getNotificationCount = async () => {
    try {
      const response = await axios.get(
        `/v1/notification/count`,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem(
              "serviceToken"
            )}`,
            "Content-Type": "application/json",
            requestId: RequestId(
              Math.floor(1000 + Math.random() * 9999),
              Math.floor(1000 + Math.random() * 9999 + rendomRequestId)
            ),
          },
        }
      );
      setCount(response?.data?.data)
      generateTimeStamp();
    } catch (error) {
      generateTimeStamp();
      console.error("Failed to fetch data:", error);
    }
  }

  useEffect(() => {
    getNotificationCount()
  }, [open])

  return (
    <>
      <Box
        sx={{
          position: "relative",
          ml: 1,
          mr: 1,
          [theme.breakpoints.down("md")]: {
            mr: 2,
          },
        }}
      >
        <Chip
          size="small"
          label={count}
          sx={{
            position: "absolute",
            top: "0px",
            right: "0px",
            transform: "translate(50%, -50%)",
            color: theme.palette.background.default,
            bgcolor: theme.palette.warning.dark,
            zIndex: 1,
          }}
        />
        <Avatar
          variant="rounded"
          sx={{
            ...theme.typography.commonAvatar,
            ...theme.typography.mediumAvatar,
            transition: "all .2s ease-in-out",
            background:
              theme.palette.mode === "dark"
                ? theme.palette.dark.main
                : theme.palette.secondary.light,
            color:
              theme.palette.mode === "dark"
                ? theme.palette.warning.dark
                : theme.palette.secondary.dark,
            '&[aria-controls="menu-list-grow"],&:hover': {
              background:
                theme.palette.mode === "dark"
                  ? theme.palette.warning.dark
                  : theme.palette.secondary.dark,
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.grey[800]
                  : theme.palette.secondary.light,
            },
          }}
          ref={anchorRef}
          aria-controls={open ? "menu-list-grow" : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          color="inherit"
        >
          <IconBell stroke={1.5} size="20px" />
        </Avatar>
      </Box>

      <Popper
        placement={matchesXs ? "bottom" : "bottom-end"}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [matchesXs ? 5 : 0, 20],
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions
              position={matchesXs ? "top" : "top-right"}
              in={open}
              {...TransitionProps}
            >
              <Paper>
                {open && (
                  <MainCard
                    border={false}
                    elevation={16}
                    content={false}
                    boxShadow
                    shadow={theme.shadows[16]}
                  >
                    <Grid container direction="column" spacing={2}>
                      <Grid item xs={12}>
                        <Grid
                          container
                          alignItems="center"
                          justifyContent="space-between"
                        //   sx={{ pt: 1, px: 1 }}
                        ></Grid>
                      </Grid>
                      <Grid item xs={12}>
                        <PerfectScrollbar
                          style={{
                            height: "100%",
                            maxHeight: "calc(100vh - 205px)",
                            overflowX: "hidden",
                          }}
                        >
                          <NotificationList />
                        </PerfectScrollbar>
                      </Grid>
                    </Grid>
                    <Divider />
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
};

export default NotificationSection;
